import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import toast from 'react-hot-toast';

export default function CreditStore() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('packages');
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/credits/packages'),
      api.get('/api/credits/history').catch(() => ({ data: { transactions: [] } })),
    ]).then(([p, h]) => {
      setPackages(p.data.packages || []);
      setHistory(h.data.transactions || []);
      setLoading(false);
    }).catch(() => setLoading(false));

    // Check if returning from Stripe
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId, 0);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt) => {
    if (attempt >= 8) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await api.get(`/api/credits/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success(`Credits added! ${res.data.metadata?.credits || ''} credits purchased.`);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Payment session expired');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 3000);
    }
  };

  const buyCredits = async (packageId) => {
    setPurchasing(packageId);
    try {
      const origin = window.location.origin;
      const res = await api.post('/api/credits/checkout', { package_id: packageId, origin_url: origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const icons = { starter: 'fa-bolt', pro: 'fa-rocket', enterprise: 'fa-building' };
  const gradients = {
    starter: 'from-sky-400 to-blue-600',
    pro: 'from-primary to-emerald-600',
    enterprise: 'from-violet-500 to-purple-700',
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="credit-store-page">
      {paymentStatus === 'success' && (
        <div className="bg-primary/10 border border-primary/30 rounded-2xl p-6 text-center animate-scale-in" data-testid="payment-success-banner">
          <i className="fa-solid fa-circle-check text-4xl text-primary mb-3" />
          <h3 className="font-heading text-lg font-bold text-primary">Payment Successful!</h3>
          <p className="text-sm text-text-secondary mt-1">Credits have been added to your account.</p>
        </div>
      )}
      {paymentStatus === 'checking' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center animate-pulse" data-testid="payment-checking">
          <i className="fa-solid fa-spinner fa-spin text-2xl text-yellow-600 mb-2" />
          <p className="text-sm text-yellow-700 font-medium">Verifying your payment...</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Credit Store</h1>
          <p className="text-text-muted text-sm mt-1">Power up your lead generation with credits</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary/10 to-emerald-50 border border-primary/20">
            <i className="fa-solid fa-coins text-primary" />
            <span className="font-heading text-xl font-bold text-primary tabular-nums" data-testid="current-credits">{user?.credits || 0}</span>
            <span className="text-xs text-text-muted font-medium">credits</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {['packages', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-5 py-3 text-sm font-medium capitalize transition-all border-b-2 ${tab === t ? 'text-primary border-primary' : 'text-text-muted border-transparent hover:text-text-primary'}`} data-testid={`tab-${t}`}>
            {t === 'packages' ? 'Buy Credits' : 'Purchase History'}
          </button>
        ))}
      </div>

      {tab === 'packages' && (
        <>
          {/* What Credits Get You */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: 'fa-satellite-dish', label: 'Scan Leads', cost: '1 credit/lead', color: 'primary' },
              { icon: 'fa-brain', label: 'AI Enrichment', cost: '5 credits/lead', color: 'secondary' },
              { icon: 'fa-file-invoice', label: 'Proposals', cost: '3 credits each', color: 'accent' },
              { icon: 'fa-envelope', label: 'Email Delivery', cost: '2 credits each', color: 'primary' },
            ].map(item => (
              <div key={item.label} className="card-interactive p-4 text-center">
                <div className={`w-10 h-10 rounded-xl bg-${item.color}/10 flex items-center justify-center mx-auto mb-2`}>
                  <i className={`fa-solid ${item.icon} text-${item.color}`} />
                </div>
                <p className="text-sm font-semibold text-text-primary">{item.label}</p>
                <p className="text-[10px] text-text-muted mt-0.5">{item.cost}</p>
              </div>
            ))}
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {packages.map((pkg, i) => (
              <div key={pkg.id} className={`relative rounded-2xl border transition-all duration-300 overflow-hidden animate-slide-up ${pkg.popular ? 'border-primary shadow-glow-green scale-[1.02]' : 'border-border hover:border-primary/20 hover:shadow-card-hover'}`} style={{ animationDelay: `${i * 80}ms` }} data-testid={`package-${pkg.id}`}>
                {pkg.popular && (
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary to-emerald-500" />
                )}
                <div className={`p-3 bg-gradient-to-br ${gradients[pkg.id]} text-white text-center`}>
                  <i className={`fa-solid ${icons[pkg.id]} text-2xl mb-1 opacity-80`} />
                  <h3 className="font-heading text-lg font-bold">{pkg.name}</h3>
                </div>
                <div className="p-6 bg-surface">
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-heading text-4xl font-bold text-text-primary">${pkg.price}</span>
                    </div>
                    <p className="text-sm text-text-muted mt-1">{pkg.credits.toLocaleString()} credits</p>
                    <p className="text-xs text-primary font-semibold mt-0.5">${(pkg.price / pkg.credits * 100).toFixed(1)}c per credit</p>
                  </div>

                  <div className="space-y-2 mb-6">
                    {[
                      `${pkg.credits.toLocaleString()} mining credits`,
                      `${Math.floor(pkg.credits / 5)} AI enrichments`,
                      `${Math.floor(pkg.credits / 3)} proposals`,
                      pkg.id === 'enterprise' ? 'Priority support' : 'Email support',
                      pkg.popular ? 'Best value' : null,
                    ].filter(Boolean).map(f => (
                      <div key={f} className="flex items-center gap-2 text-sm text-text-secondary">
                        <i className="fa-solid fa-check text-primary text-xs" />{f}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => buyCredits(pkg.id)}
                    disabled={purchasing === pkg.id}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${pkg.popular ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {purchasing === pkg.id ? (
                      <><i className="fa-solid fa-spinner fa-spin mr-2" />Processing...</>
                    ) : (
                      <><i className="fa-solid fa-credit-card mr-2" />Buy Now</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Trust */}
          <div className="text-center py-6">
            <div className="flex items-center justify-center gap-6 text-text-muted">
              <span className="flex items-center gap-1.5 text-xs"><i className="fa-solid fa-lock text-primary" />Secure Stripe Checkout</span>
              <span className="flex items-center gap-1.5 text-xs"><i className="fa-solid fa-shield-halved text-primary" />256-bit Encryption</span>
              <span className="flex items-center gap-1.5 text-xs"><i className="fa-solid fa-rotate-left text-primary" />Instant Credit Delivery</span>
            </div>
          </div>
        </>
      )}

      {tab === 'history' && (
        <div className="card-interactive overflow-hidden">
          {history.length === 0 ? (
            <div className="text-center py-16">
              <i className="fa-solid fa-receipt text-4xl text-text-muted opacity-20 mb-4 block" />
              <p className="text-text-muted font-medium">No purchases yet</p>
              <p className="text-text-muted text-sm mt-1">Your purchase history will appear here</p>
            </div>
          ) : (
            <table className="w-full" data-testid="history-table">
              <thead>
                <tr className="border-b border-border">
                  {['Package', 'Credits', 'Amount', 'Status', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[10px] font-bold uppercase tracking-widest text-text-muted bg-surface-alt">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((txn, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-surface-alt/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-text-primary">{txn.package_name}</td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-primary tabular-nums">{txn.credits?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-text-primary">${txn.amount?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${txn.payment_status === 'completed' ? 'bg-primary/10 text-primary' : txn.payment_status === 'pending' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${txn.payment_status === 'completed' ? 'bg-primary' : txn.payment_status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        {txn.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-text-muted">{new Date(txn.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
